/**
 * Master enum catalog (M3X.UI-HYGIENE-PASS-2 PR 1).
 *
 * Single source of truth for every backend-CHECK-constrained enum
 * field. `<EnumSelect kind="X">` reads from this catalog. The backend
 * sync test (`constitution/backend/test_enum_catalog_sync.py`)
 * catches drift between this file and the backend Pydantic enums.
 *
 * Adding a new enum kind:
 * 1. Extend `EnumKind` in `types.ts`
 * 2. Add the definition below
 * 3. Confirm the backend Pydantic enum agrees with the value set
 */

import type { EnumDefinition, EnumKind } from "./types";

export const ENUM_CATALOG: Record<EnumKind, EnumDefinition> = {
  employment_type: {
    kind: "employment_type",
    options: [
      {
        value: "saudi",
        labelEn: "Saudi national",
        labelAr: "مواطن سعودي",
        badge: "info",
      },
      {
        value: "non_saudi_gcc",
        labelEn: "Non-Saudi GCC national",
        labelAr: "مواطن خليجي",
      },
      {
        value: "non_saudi_non_gcc",
        labelEn: "Non-Saudi non-GCC",
        labelAr: "غير سعودي وغير خليجي",
      },
    ],
  },

  contract_period: {
    kind: "contract_period",
    options: [
      {
        value: "unlimited",
        labelEn: "Unlimited (open-ended)",
        labelAr: "غير محدد",
        description: "No end date · KSA Labor Article 55",
        descriptionAr: "بدون تاريخ انتهاء · نظام العمل المادة 55",
      },
      {
        value: "fixed_term",
        labelEn: "Fixed term",
        labelAr: "محدد المدة",
        description: "End date required",
        descriptionAr: "تاريخ الانتهاء مطلوب",
      },
    ],
  },

  contract_status: {
    kind: "contract_status",
    options: [
      { value: "draft", labelEn: "Draft", labelAr: "مسودة", badge: "default" },
      { value: "active", labelEn: "Active", labelAr: "نشط", badge: "success" },
      {
        value: "suspended",
        labelEn: "Suspended",
        labelAr: "موقّف",
        badge: "warning",
      },
      {
        value: "terminated",
        labelEn: "Terminated",
        labelAr: "منتهي",
        badge: "default",
      },
    ],
  },

  marital_status: {
    kind: "marital_status",
    options: [
      { value: "single", labelEn: "Single", labelAr: "أعزب/عزباء" },
      { value: "married", labelEn: "Married", labelAr: "متزوج/متزوجة" },
      { value: "divorced", labelEn: "Divorced", labelAr: "مطلق/مطلقة" },
      { value: "widowed", labelEn: "Widowed", labelAr: "أرمل/أرملة" },
    ],
    allowEmpty: true,
    emptyLabelEn: "Not specified",
    emptyLabelAr: "غير محدد",
  },

  gender: {
    kind: "gender",
    options: [
      { value: "male", labelEn: "Male", labelAr: "ذكر" },
      { value: "female", labelEn: "Female", labelAr: "أنثى" },
    ],
  },

  payment_method: {
    kind: "payment_method",
    options: [
      {
        value: "bank_transfer",
        labelEn: "Bank transfer",
        labelAr: "تحويل بنكي",
        description: "WPS via Mudad",
        descriptionAr: "نظام حماية الأجور عبر مدد",
      },
      { value: "cheque", labelEn: "Cheque", labelAr: "شيك" },
      {
        value: "cash",
        labelEn: "Cash",
        labelAr: "نقدي",
        badge: "warning",
        description: "Discouraged · KSA Labor Article 90",
        descriptionAr: "غير مستحسن · نظام العمل المادة 90",
      },
    ],
  },

  religion: {
    kind: "religion",
    options: [
      { value: "muslim", labelEn: "Muslim", labelAr: "مسلم/مسلمة" },
      {
        value: "non_muslim",
        labelEn: "Non-Muslim",
        labelAr: "غير مسلم/غير مسلمة",
      },
    ],
    allowEmpty: true,
    emptyLabelEn: "Not specified",
    emptyLabelAr: "غير محدد",
  },

  workflow_state_leave: {
    kind: "workflow_state_leave",
    options: [
      { value: "draft", labelEn: "Draft", labelAr: "مسودة" },
      { value: "submitted", labelEn: "Submitted", labelAr: "مُقدَّم" },
      {
        value: "pending_approval",
        labelEn: "Pending approval",
        labelAr: "في انتظار الموافقة",
      },
      {
        value: "approved",
        labelEn: "Approved",
        labelAr: "موافَق عليه",
        badge: "success",
      },
      {
        value: "denied",
        labelEn: "Denied",
        labelAr: "مرفوض",
        badge: "warning",
      },
      { value: "cancelled", labelEn: "Cancelled", labelAr: "ملغى" },
    ],
  },

  workflow_state_loan: {
    kind: "workflow_state_loan",
    options: [
      { value: "draft", labelEn: "Draft", labelAr: "مسودة" },
      { value: "submitted", labelEn: "Submitted", labelAr: "مُقدَّم" },
      {
        value: "eligibility_check",
        labelEn: "Eligibility check",
        labelAr: "فحص الأهلية",
      },
      {
        value: "pending_approval",
        labelEn: "Pending approval",
        labelAr: "في انتظار الموافقة",
      },
      {
        value: "approved",
        labelEn: "Approved",
        labelAr: "موافَق عليه",
        badge: "success",
      },
      {
        value: "disbursed",
        labelEn: "Disbursed",
        labelAr: "مصروف",
        badge: "info",
      },
      {
        value: "in_repayment",
        labelEn: "In repayment",
        labelAr: "قيد السداد",
      },
      {
        value: "settled",
        labelEn: "Settled",
        labelAr: "مسوّى",
        badge: "success",
      },
      {
        value: "denied",
        labelEn: "Denied",
        labelAr: "مرفوض",
        badge: "warning",
      },
      { value: "cancelled", labelEn: "Cancelled", labelAr: "ملغى" },
    ],
  },

  workflow_state_termination: {
    kind: "workflow_state_termination",
    options: [
      { value: "initiated", labelEn: "Initiated", labelAr: "بدأ" },
      {
        value: "settlement_calculated",
        labelEn: "Settlement calculated",
        labelAr: "تم حساب التسوية",
      },
      {
        value: "pending_hr_review",
        labelEn: "Pending HR review",
        labelAr: "في انتظار مراجعة الموارد البشرية",
      },
      {
        value: "approved",
        labelEn: "Approved",
        labelAr: "موافَق عليه",
        badge: "success",
      },
      {
        value: "exit_clearance_in_progress",
        labelEn: "Exit clearance in progress",
        labelAr: "إخلاء الطرف قيد التنفيذ",
      },
      {
        value: "gov_filings_drafted",
        labelEn: "Government filings drafted",
        labelAr: "تم تجهيز الإيداعات الحكومية",
      },
      {
        value: "final_payslip_generated",
        labelEn: "Final payslip generated",
        labelAr: "تم إصدار قسيمة الراتب النهائية",
      },
      { value: "closed", labelEn: "Closed", labelAr: "مغلق" },
      {
        value: "denied",
        labelEn: "Denied",
        labelAr: "مرفوض",
        badge: "warning",
      },
      { value: "cancelled", labelEn: "Cancelled", labelAr: "ملغى" },
    ],
  },

  workflow_state_excuse: {
    kind: "workflow_state_excuse",
    options: [
      { value: "draft", labelEn: "Draft", labelAr: "مسودة" },
      { value: "submitted", labelEn: "Submitted", labelAr: "مُقدَّم" },
      {
        value: "pending_approval",
        labelEn: "Pending approval",
        labelAr: "في انتظار الموافقة",
      },
      {
        value: "approved",
        labelEn: "Approved",
        labelAr: "موافَق عليه",
        badge: "success",
      },
      {
        value: "recorded",
        labelEn: "Recorded",
        labelAr: "مُسجَّل",
        badge: "success",
      },
      {
        value: "denied",
        labelEn: "Denied",
        labelAr: "مرفوض",
        badge: "warning",
      },
      { value: "cancelled", labelEn: "Cancelled", labelAr: "ملغى" },
    ],
  },

  leave_category: {
    kind: "leave_category",
    options: [
      { value: "annual", labelEn: "Annual", labelAr: "سنوي" },
      { value: "sick", labelEn: "Sick", labelAr: "مرضي" },
      { value: "maternity", labelEn: "Maternity", labelAr: "أمومة" },
      { value: "paternity", labelEn: "Paternity", labelAr: "أبوة" },
      { value: "hajj", labelEn: "Hajj", labelAr: "حج" },
      { value: "marriage", labelEn: "Marriage", labelAr: "زواج" },
      { value: "iddah", labelEn: "Iddah", labelAr: "عدة" },
      { value: "bereavement", labelEn: "Bereavement", labelAr: "وفاة" },
      { value: "exam", labelEn: "Exam", labelAr: "امتحان" },
      { value: "emergency", labelEn: "Emergency", labelAr: "طارئ" },
      { value: "unpaid", labelEn: "Unpaid", labelAr: "بدون أجر" },
      { value: "hardship_travel", labelEn: "Hardship travel", labelAr: "سفر اضطراري" },
    ],
  },

  loan_repayment_frequency: {
    kind: "loan_repayment_frequency",
    options: [
      { value: "monthly", labelEn: "Monthly", labelAr: "شهري" },
      { value: "weekly", labelEn: "Weekly", labelAr: "أسبوعي" },
      { value: "biweekly", labelEn: "Bi-weekly", labelAr: "نصف شهري" },
      { value: "quarterly", labelEn: "Quarterly", labelAr: "ربع سنوي" },
    ],
  },

  excuse_category: {
    kind: "excuse_category",
    options: [
      { value: "late", labelEn: "Late arrival", labelAr: "تأخر" },
      {
        value: "early",
        labelEn: "Early departure",
        labelAr: "خروج مبكر",
      },
      { value: "permission", labelEn: "Permission", labelAr: "إذن" },
      { value: "sick", labelEn: "Sick (half-day)", labelAr: "مرضي (نصف يوم)" },
      { value: "personal", labelEn: "Personal", labelAr: "شخصي" },
    ],
  },

  position_status: {
    kind: "position_status",
    options: [
      { value: "draft", labelEn: "Draft", labelAr: "مسودة" },
      { value: "open", labelEn: "Open", labelAr: "متاح" },
      { value: "filled", labelEn: "Filled", labelAr: "مشغول", badge: "success" },
      {
        value: "abolished",
        labelEn: "Abolished",
        labelAr: "ملغى",
        badge: "default",
      },
    ],
  },

  termination_reason: {
    kind: "termination_reason",
    options: [
      { value: "resignation", labelEn: "Resignation", labelAr: "استقالة" },
      {
        value: "with_cause",
        labelEn: "With cause (Article 80)",
        labelAr: "بسبب (المادة 80)",
        badge: "warning",
      },
      {
        value: "without_cause",
        labelEn: "Without cause",
        labelAr: "بدون سبب",
      },
      {
        value: "end_of_fixed_term",
        labelEn: "End of fixed term",
        labelAr: "نهاية المدة المحددة",
      },
      { value: "mutual", labelEn: "Mutual agreement", labelAr: "اتفاق متبادل" },
      { value: "deceased", labelEn: "Deceased", labelAr: "متوفى" },
      {
        value: "incapacitated",
        labelEn: "Incapacitated",
        labelAr: "غير قادر",
      },
      {
        value: "absconded",
        labelEn: "Absconded",
        labelAr: "هارب",
        badge: "danger",
      },
      {
        value: "regulatory",
        labelEn: "Regulatory",
        labelAr: "تنظيمي",
      },
    ],
  },

  currency_code: {
    kind: "currency_code",
    options: [
      { value: "SAR", labelEn: "Saudi Riyal (SAR)", labelAr: "ريال سعودي" },
      { value: "USD", labelEn: "US Dollar (USD)", labelAr: "دولار أمريكي" },
      { value: "EUR", labelEn: "Euro (EUR)", labelAr: "يورو" },
      { value: "GBP", labelEn: "British Pound (GBP)", labelAr: "جنيه استرليني" },
      { value: "AED", labelEn: "UAE Dirham (AED)", labelAr: "درهم إماراتي" },
    ],
  },

  saudization_classification: {
    kind: "saudization_classification",
    options: [
      { value: "saudi", labelEn: "Saudi", labelAr: "سعودي", badge: "success" },
      {
        value: "non_saudi",
        labelEn: "Non-Saudi",
        labelAr: "غير سعودي",
      },
      {
        value: "saudi_dependent",
        labelEn: "Saudi dependent",
        labelAr: "تابع سعودي",
        description: "Counts toward Nitaqat",
        descriptionAr: "يحسب ضمن نطاقات",
      },
    ],
    allowEmpty: true,
    emptyLabelEn: "Not specified",
    emptyLabelAr: "غير محدد",
  },
};

/** Look up a definition by kind. */
export function getEnumDefinition(kind: EnumKind): EnumDefinition {
  return ENUM_CATALOG[kind];
}

/** Find an option by value within a kind. */
export function findEnumOption(kind: EnumKind, value: string | null | undefined) {
  if (!value) return null;
  return ENUM_CATALOG[kind].options.find((o) => o.value === value) ?? null;
}
